import { mapGlobalContactApiToUi } from '@/lib/mappers/customer-detail';
import { mockCustomers } from '@/lib/mock-customers';
import type { GlobalContactApi } from '@/types/api/customer-detail';
import type { GlobalContactsQueryParams, GlobalContactUI } from '@/types/ui/customer-detail';
import { getAllContacts } from './customer-communications-contacts';

/**
 * Demo data for the Global Contacts screen.
 * Joins the per-customer mock contacts with mock customer records so that each
 * contact carries its customer's name and account number.
 */
export function getAllGlobalContacts(): GlobalContactApi[] {
  const customerById = new Map(mockCustomers.map((customer) => [customer.id, customer]));

  return getAllContacts().map((contact) => {
    const customer = customerById.get(contact.customerId);
    return {
      ...contact,
      customerName: customer?.name ?? '',
      customerAccountNumber: customer?.accountNumber ?? '',
    };
  });
}

function matchesSearch(contact: GlobalContactUI, search: string): boolean {
  const haystack = [
    contact.firstName,
    contact.surname,
    contact.email,
    contact.phone,
    contact.mobile ?? '',
    contact.jobTitle,
    contact.customerName,
    contact.customerAccountNumber,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(search.toLowerCase());
}

function compareContacts(a: GlobalContactUI, b: GlobalContactUI, sortField: string): number {
  const sortValue = (contact: GlobalContactUI): string => {
    switch (sortField) {
      case 'firstName':
        return contact.firstName;
      case 'email':
        return contact.email;
      case 'phone':
        return contact.phone;
      case 'mobile':
        return contact.mobile ?? '';
      case 'jobTitle':
        return contact.jobTitle;
      case 'company':
        return contact.company;
      case 'customerName':
        return contact.customerName;
      case 'customerAccountNumber':
        return contact.customerAccountNumber;
      case 'lastContact':
        return contact.lastContact ?? '';
      case 'createdAt':
        return contact.createdAt;
      default:
        return contact.surname;
    }
  };
  return sortValue(a).localeCompare(sortValue(b));
}

/**
 * Applies the same filtering/sorting semantics as the backend global contacts
 * query to the demo dataset. Risk-level filtering is not represented in demo
 * data, so that filter is ignored in demo mode.
 */
export function getFilteredGlobalContactsUI(params: GlobalContactsQueryParams): GlobalContactUI[] {
  let contacts = getAllGlobalContacts().map(mapGlobalContactApiToUi);

  if (params.search?.trim()) {
    const search = params.search.trim();
    contacts = contacts.filter((contact) => matchesSearch(contact, search));
  }

  if (params.customerIds && params.customerIds.length > 0) {
    const ids = new Set(params.customerIds);
    contacts = contacts.filter((contact) => ids.has(contact.customerId));
  }

  if (params.primaryOnly) {
    contacts = contacts.filter((contact) => contact.isPrimary);
  }

  if (params.hasEmail) {
    contacts = contacts.filter((contact) => contact.email.trim() !== '');
  }

  const sortField = params.sortField ?? 'surname';
  const direction = params.sortDirection === 'desc' ? -1 : 1;
  contacts = [...contacts].sort((a, b) => compareContacts(a, b, sortField) * direction);

  return contacts;
}
